import { Controller } from "@hotwired/stimulus";


export default class extends Controller {
  static targets = ["input", "makeId", "dropdown"];

  connect() {
    this._selectedMakeId = null;
    this._selectedMakeName = null;
    this._highlightedIndex = -1;
    this._results = [];
    this._blurTimer = null;

    this._debouncedSearch = this._debounce(this._search.bind(this), 300);

    this.inputTarget.addEventListener("input", this._onInput.bind(this));
    this.inputTarget.addEventListener("keydown", this._onKeydown.bind(this));
    this.inputTarget.addEventListener("blur", this._onBlur.bind(this));
    this.inputTarget.addEventListener("focus", this._onFocus.bind(this));
  }

  disconnect() {
    clearTimeout(this._blurTimer);
  }

  // ─── Event handlers ──────────────────────────────────────────────────────

  _onInput() {
    const query = this.inputTarget.value.trim();

    // Any typing clears a previously selected make_id (new-make path)
    this._clearSelection();

    if (query.length === 0) {
      this._closeDropdown();
      return;
    }

    this._debouncedSearch(query);
  }

  _onKeydown(event) {
    const items = this.dropdownTarget.querySelectorAll("[data-make-option]");

    if (!this._isOpen()) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this._highlightedIndex = Math.min(
          this._highlightedIndex + 1,
          items.length - 1
        );
        this._applyHighlight(items);
        break;

      case "ArrowUp":
        event.preventDefault();
        this._highlightedIndex = Math.max(this._highlightedIndex - 1, 0);
        this._applyHighlight(items);
        break;

      case "Enter":
        if (this._highlightedIndex >= 0 && items[this._highlightedIndex]) {
          event.preventDefault();
          items[this._highlightedIndex].click();
        }
        break;

      case "Escape":
        this._closeDropdown();
        this.inputTarget.blur();
        break;
    }
  }

  _onBlur() {
    // Delay so a dropdown item click fires before we close
    this._blurTimer = setTimeout(() => {
      this._closeDropdown();
      // blur-to-confirm: whatever is in the input box becomes make_text
      // make_id stays null if nothing was explicitly selected — that's correct
    }, 150);
  }

  _onFocus() {
    // Re-open dropdown if there's a query and results cached
    const query = this.inputTarget.value.trim();
    if (query.length > 0 && this._results.length > 0) {
      this._renderDropdown(query, this._results);
    }
  }

  // ─── Search ───────────────────────────────────────────────────────────────

  async _search(query) {
    try {
      const url = `/api/makes/search?q=${encodeURIComponent(query)}`;
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const makes = await response.json();
      this._results = makes;
      this._renderDropdown(query, makes);
    } catch (_err) {
      // Network error — fail silently, user can still type a new make
    }
  }

  // ─── Dropdown rendering ──────────────────────────────────────────────────

  _renderDropdown(query, makes) {
    const dropdown = this.dropdownTarget;
    dropdown.innerHTML = "";
    this._highlightedIndex = -1;

    // Check for exact match (case-insensitive) to decide whether to show "add" row
    const lowerQuery = query.toLowerCase();
    const exactMatch = makes.some((m) => m.name.toLowerCase() === lowerQuery);

    if (makes.length > 0) {
      makes.forEach((make, idx) => {
        const item = document.createElement("div");
        item.className = "mac-option";
        item.setAttribute("data-make-option", "");
        item.setAttribute("data-make-id", make.id);
        item.setAttribute("data-make-name", make.name);
        item.setAttribute("role", "option");
        item.setAttribute("aria-selected", "false");
        item.innerHTML = this._highlightMatch(make.name, query);
        item.addEventListener("mousedown", (e) => {
          e.preventDefault(); // prevent blur from firing first
          this._selectMake(make.id, make.name);
        });
        dropdown.appendChild(item);
      });
    } else {
      const noResults = document.createElement("div");
      noResults.className = "mac-no-results";
      noResults.textContent = "No matches - type a new make";
      dropdown.appendChild(noResults);
    }

    // "Add as new make" row — shown whenever there is no exact match
    if (!exactMatch) {
      const addRow = document.createElement("div");
      addRow.className = "mac-add-row";
      addRow.setAttribute("data-make-option", "");
      addRow.setAttribute("data-make-id", "");
      addRow.setAttribute("data-make-name", query);
      addRow.setAttribute("role", "option");
      addRow.innerHTML = `<svg class="mac-add-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/></svg>Add <strong>${this._escapeHtml(query)}</strong> as new make`;
      addRow.addEventListener("mousedown", (e) => {
        e.preventDefault();
        this._selectNewMake(query);
      });
      dropdown.appendChild(addRow);
    }

    this._openDropdown();
  }

  // Wrap matched characters in <mark> for visual highlight
  _highlightMatch(name, query) {
    const escapedName = this._escapeHtml(name);
    const escapedQuery = this._escapeHtml(query);
    const regexEscaped = escapedQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${regexEscaped})`, "gi");
    return escapedName.replace(regex, "<mark>$1</mark>");
  }

  _escapeHtml(str) {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  _applyHighlight(items) {
    items.forEach((item, idx) => {
      item.classList.toggle("mac-option--highlighted", idx === this._highlightedIndex);
    });
    // Scroll highlighted item into view
    if (items[this._highlightedIndex]) {
      items[this._highlightedIndex].scrollIntoView({ block: "nearest" });
    }
  }

  // ─── Selection ────────────────────────────────────────────────────────────

  _selectMake(id, name) {
    this._selectedMakeId = id;
    this._selectedMakeName = name;
    this.makeIdTarget.value = id;
    this.inputTarget.value = name;
    this._closeDropdown();

    // Move focus to the Model field (looked up by id stored in data attribute)
    const modelInputId = this.inputTarget.dataset.modelInputId;
    if (modelInputId) {
      const modelField = document.getElementById(modelInputId);
      if (modelField) modelField.focus();
    }
  }

  _selectNewMake(name) {
    this._selectedMakeId = null;
    this._selectedMakeName = name;
    this.makeIdTarget.value = "";
    this.inputTarget.value = name;
    this._closeDropdown();
  }

  _clearSelection() {
    this._selectedMakeId = null;
    this.makeIdTarget.value = "";
  }

  // ─── Dropdown open/close ─────────────────────────────────────────────────

  _openDropdown() {
    this.dropdownTarget.classList.add("mac-dropdown--open");
  }

  _closeDropdown() {
    this.dropdownTarget.classList.remove("mac-dropdown--open");
    this._highlightedIndex = -1;
  }

  _isOpen() {
    return this.dropdownTarget.classList.contains("mac-dropdown--open");
  }

  // ─── Internal debounce ───────────────────────────────────────────────────

  _debounce(fn, ms) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  }
}
